import { getCreds } from 'common/axios/jwt/creds';
import { refreshToken } from 'common/axios/jwt/refresh';
import { setConfig } from 'common/axios/jwt/state';
import { ETokenStatus, IJwtInterceptorsConfig, PRE_REFRESH_PERIOD_SEC } from 'common/axios/jwt/types';

export const configureJwtInterceptors = (config: IJwtInterceptorsConfig): void => {
  setConfig(config);

  // Restore Authorization header on app reload if creds already exist in localStorage.
  const initial = getCreds();
  if (initial.access?.token) {
    config.axios.defaults.headers.common.Authorization = `Bearer ${initial.access.token}`;
  }

  config.axios.interceptors.request.use(async (requestConfig) => {
    const { access, refresh } = getCreds();
    const now = Date.now() / 1000;

    if (refresh?.expiredAt && refresh.expiredAt > now) {
      if (access?.expiredAt && access.expiredAt < now) {
        await refreshToken(config).catch(() => {
          /* noop: failure propagates via response-interceptor or next request */
        });
      } else if (access?.expiredAt && access.expiredAt - now < PRE_REFRESH_PERIOD_SEC) {
        refreshToken(config).catch(() => {
          /* noop: proactive background refresh */
        });
      }
    }

    const auth = config.axios.defaults.headers.common['Authorization'];
    if (auth) {
      requestConfig.headers['Authorization'] = auth as string;
    }

    return requestConfig;
  });

  config.axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error?.response?.status === 401 && error.response?.data?.code === ETokenStatus.Expired) {
        await refreshToken(config);
        // Drop stale Authorization so the request-interceptor re-stamps it with the refreshed token.
        if (error.config?.headers?.Authorization) {
          delete error.config.headers.Authorization;
        }
        return config.axios(error.config);
      }
      throw error;
    },
  );
};
